import React from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
// Direct module, not the `app/components` barrel: the barrel's import graph
// cycles back into this subtree, so `Tooltip` (a const) is still uninitialized
// when this module evaluates and renders as undefined.
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import {
  useAction,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { useArtifactPanelModel } from './artifact-panel.model';
import {
  usePanelUnifiedChrome,
  useUnifiedBarRendered,
} from './artifact-panel-context';
import { ExpandArtifactPanelAction } from './actions/expand-artifact-panel.action';
import { CollapseArtifactPanelAction } from './actions/collapse-artifact-panel.action';

const NULL_EXPANDED_WATCHABLE = Watchable.fromValue(false);
const NULL_CAN_EXPAND_WATCHABLE = Watchable.fromValue(false);

/**
 * True when this subtree is the one that should draw the panel's expand toggle:
 * there is something to toggle, and no ancestor has already rendered the
 * unified bar, which hosts the toggle itself. Without the second half an
 * artifact renderer that draws its own chrome stacks a second copy of the same
 * control under the bar's.
 *
 * `canExpand` alone is not the question. It answers "is there a split to widen",
 * which is false once an artifact is focused — there is nothing left to widen,
 * the artifact already fills the chat. But that is exactly when the control has
 * the most to offer, because it is the way back out. So focus counts too, and
 * on any viewport: the in-place expand is desktop-only, leaving focus is not.
 */
const useOwnsExpandToggle = (): boolean => {
  const artifactPanelModel = useArtifactPanelModel();
  const canExpand = useWatchableValue(
    artifactPanelModel?.canExpandWatchable ?? NULL_CAN_EXPAND_WATCHABLE
  );
  const chrome = usePanelUnifiedChrome();
  const barAlreadyRendered = useUnifiedBarRendered();
  return (
    artifactPanelModel !== null &&
    (canExpand === true || chrome?.focusView !== undefined) &&
    barAlreadyRendered === false
  );
};

export const ArtifactPanelExpandButton: React.FC = () => {
  const artifactPanelModel = useArtifactPanelModel();
  const expandAction = useAction(ExpandArtifactPanelAction);
  const collapseAction = useAction(CollapseArtifactPanelAction);
  const ownsToggle = useOwnsExpandToggle();
  const chrome = usePanelUnifiedChrome();
  const isExpanded = useWatchableValue(
    artifactPanelModel?.isExpandedWatchable ?? NULL_EXPANDED_WATCHABLE
  );
  if (artifactPanelModel === null || ownsToggle === false) {
    return null;
  }

  // One control, one axis: wide or not. Which mechanism widens it depends on
  // what the host offers, but every branch below is the same toggle in a
  // different medium, so the icon, the `aria-pressed` and the Esc shortcut hold
  // throughout.
  //
  // Focus, where the host addresses one artifact: the artifact fills the chat
  // and the address says which one, so it can be linked and reloaded and its
  // bar belongs to the artifact rather than the conversation. Leaving it is the
  // same button, not a Back arrow — nothing was navigated to.
  //
  // In-place expand, where the host has no such address: the standalone editor
  // pages, and the workflow canvas on its way into a node. Same gesture, no
  // address to show for it.
  const artifactId = chrome?.activeArtifactId ?? null;
  const openFocusView = chrome?.openFocusView;
  const closeFocus = chrome?.focusView?.onBack;
  const isFocused = closeFocus !== undefined;
  const canFocus =
    isExpanded !== true && openFocusView !== undefined && artifactId !== null;

  const isWide = isFocused || isExpanded === true;
  const Icon = isWide ? Minimize2 : Maximize2;
  const label = isFocused
    ? 'Close focus view'
    : isExpanded === true
    ? 'Collapse artifact panel'
    : canFocus
    ? 'Focus view'
    : 'Expand artifact panel';
  // The Esc shortcut used to be spelled out inline, as a `kbd` and the words
  // "to collapse" — roughly 110px of the bar, sitting immediately beside the
  // button that does the same thing. A shortcut is worth surfacing, but not at
  // the cost of permanent space next to its own control; the tooltip is where
  // the rest of the app puts them.
  const tooltip = isWide ? `${label} (Esc)` : label;

  return (
    <Tooltip side="bottom" tooltipContent={tooltip}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="size-8 shrink-0 p-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground"
        onClick={() => {
          if (closeFocus !== undefined) {
            closeFocus();
            return;
          }
          if (isExpanded === true) {
            void collapseAction.execute({});
            return;
          }
          if (canFocus) {
            openFocusView(artifactId);
            return;
          }
          void expandAction.execute({});
        }}
        aria-label={label}
        // Honestly a toggle in every state now: focus has a way back through
        // this same button, so there is no branch left that reports a second
        // state it does not have.
        aria-pressed={isWide}
      >
        <Icon className="size-4" />
      </Button>
    </Tooltip>
  );
};

/**
 * Header row hosting the expand toggle, for bare artifact renderers (charts,
 * dashboards) that draw their own chrome. Renders nothing — no row, no border —
 * when the unified panel bar above already owns the toggle, or when the panel
 * cannot expand at all (mobile), so those renderers never show an empty bar or a
 * duplicate expander.
 */
export const ArtifactPanelExpandBar: React.FC = () => {
  const ownsToggle = useOwnsExpandToggle();
  if (ownsToggle === false) {
    return null;
  }

  return (
    <div className="flex shrink-0 items-center justify-end border-b border-border/50 px-4 h-11">
      <ArtifactPanelExpandButton />
    </div>
  );
};
